import React, { useCallback, useRef, useState } from 'react';
import { Image } from '../Image/Image';
import { Video } from '../Video/Video';
import { VideoButton } from '../VideoButton/VideoButton';
import './MediaSection.scss';

/**
 * MediaSection - Standalone media block for Poly CMS.
 *
 * Renders a single piece of media, centered in a white section, in one of
 * three modes:
 * - `'image'`           — a still image at its original aspect ratio, filling
 *                         the content width (reuses <Image>).
 * - `'video'`           — a 16:9 video player: poster + click-to-play inline
 *                         playback (reuses <Video>; plays in place, no modal).
 *                         16:9 is the only supported video aspect ratio.
 * - `'backgroundVideo'` — an autoplaying, muted, looping motion asset with a
 *                         play/pause control. Autoplay is suppressed when the
 *                         user prefers reduced motion (starts paused, showing
 *                         the poster).
 *
 * Width: by default the media is capped at the standard 1216px content width
 * with 32px gutters. Set `fullBleed` to span the full browser width flush, with
 * no gutters or padding.
 */

export type MediaSectionMediaType = 'image' | 'video' | 'backgroundVideo';
export type MediaSectionImageAspectRatio = 'original' | '16:9';

/**
 * @polySemanticUsage About this block: dedicated to showcasing rich media — video, image, or
 * embedded content — as the primary focal point. Supporting text is minimal or absent. Use it
 * when the media itself is the message (e.g. product demos, brand videos, infographics).
 * Image aspect ratio: preserve the source image's original aspect ratio — keep
 * imageAspectRatio at original (the default) in almost all cases. Never set 16:9 unless the
 * source page itself shows the image cropped to a fixed 16:9 frame: the 16:9 option crops the
 * image (object-fit cover) and cuts off content at the edges. Aspect ratio applies to image
 * mode only — the video and background-video modes always render a fixed 16:9 frame.
 * Width: choose fullBleed to match the source presentation. With fullBleed the media
 * spans the full browser width edge-to-edge, flush with no side gutters and square corners;
 * without it the media is centered at the standard content width with side gutters and rounded
 * corners. Use fullBleed only when the source media spans the full page width.
 */
export interface MediaSectionProps {
  /**
   * Which kind of media to render.
   * @polyType SelectList
   * @polyOptions Image=image,Video Player=video,Background Video=backgroundVideo
   * @polyDefault image
   * @polyTranslatable false
   */
  mediaType?: MediaSectionMediaType;

  /**
   * Span the full browser width with no side gutters (media sits flush). When
   * false (default), the media is capped at the 1216px content width.
   * @polyType Boolean
   * @polyHelp Edge-to-edge across the full browser width (no gutters, square corners) — match the source layout
   */
  fullBleed?: boolean;

  // ---- Image mode (mediaType='image') ----
  /**
   * Image aspect ratio behavior. 'original' fills the content width at the
   * image's intrinsic aspect ratio. '16:9' crops the image into a fixed 16:9
   * frame using object-fit: cover.
   * @polyType SelectList
   * @polyOptions Original=original,16:9=16:9
   * @polyDefault original
   * @polyTranslatable false
   * @polyHelp Keep Original to preserve the image's own ratio; 16:9 crops the image and cuts off edges
   */
  imageAspectRatio?: MediaSectionImageAspectRatio;

  /**
   * Image URL.
   * @polyType Url
   */
  imageUrl?: string;
  /** Image alt text. Leave empty for a decorative image. */
  imageAlt?: string;

  // ---- Video player mode (mediaType='video', 16:9) ----
  /**
   * Poster/thumbnail image URL shown before play.
   * @polyType Url
   */
  posterUrl?: string;
  /** Poster image alt text / accessible label for the video. */
  posterAlt?: string;
  /**
   * Video URL — a YouTube watch/share link or an MP4.
   * @polyType Url
   */
  videoUrl?: string;

  // ---- Background video mode (mediaType='backgroundVideo') ----
  /**
   * Background video source (MP4/WebM). Plays muted and looped.
   * @polyType Url
   */
  backgroundVideoUrl?: string;
  /**
   * Poster frame shown before the background video loads and while paused.
   * @polyType Url
   */
  backgroundVideoPosterUrl?: string;
  /** Accessible label for the background video and its play/pause control. */
  backgroundVideoLabel?: string;
}

export const MediaSection: React.FC<MediaSectionProps> = ({
  mediaType = 'image',
  fullBleed = false,
  imageAspectRatio = 'original',
  imageUrl,
  imageAlt,
  posterUrl,
  posterAlt,
  videoUrl,
  backgroundVideoUrl,
  backgroundVideoPosterUrl,
  backgroundVideoLabel,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  // `isPlaying` mirrors the <video>'s ACTUAL playback state — it is only set
  // from the element's own 'play'/'pause' events (wired below), never
  // optimistically, so the control can't show a state the video isn't in.
  const [isPlaying, setIsPlaying] = useState(false);

  // Callback ref: the instant the <video> mounts, set muted at the ATTRIBUTE
  // level via defaultMuted. The browser's autoplay gate reads the muted
  // attribute, but React only sets the muted *property* (which lands too late),
  // so Chrome would start the video and then immediately pause it — the flash.
  // With the attribute set up front, muted autoplay sustains. Then start
  // playback unless the user prefers reduced motion.
  const setVideoRef = useCallback((node: HTMLVideoElement | null) => {
    videoRef.current = node;
    if (!node) return;
    try {
      node.defaultMuted = true;
      node.muted = true;
    } catch {
      /* ignore */
    }
    const prefersReduced =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return;
    try {
      const result = node.play();
      if (result && typeof result.then === 'function') {
        // Autoplay blocked — stays paused on the poster frame.
        result.catch(() => {});
      }
    } catch {
      /* Media playback unavailable in this environment — ignore. */
    }
  }, []);

  // Toggle acts directly on the element; the 'play'/'pause' events update
  // isPlaying, so the icon always reflects real playback state.
  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    try {
      if (video.paused) {
        video.muted = true;
        const result = video.play();
        if (result && typeof result.then === 'function') {
          result.catch(() => {});
        }
      } else {
        video.pause();
      }
    } catch {
      /* Media playback unavailable in this environment — ignore. */
    }
  }, []);

  let media: React.ReactNode = null;

  if (mediaType === 'image' && imageUrl) {
    const isLocked16x9 = imageAspectRatio === '16:9';
    const mediaClasses = [
      'media-section__media',
      isLocked16x9 ? 'media-section__media--16x9' : '',
    ].filter(Boolean).join(' ');
    // Image defaults to scale-down, which never enlarges: a source narrower than
    // the column would sit at its natural size inside a full-width box, with empty
    // bands around it. Contain scales up to fill that box, which already has the
    // image's own ratio, so nothing is cropped.
    media = (
      <div className={mediaClasses}>
        <Image url={imageUrl} alt={imageAlt || ''} objectFit={isLocked16x9 ? 'cover' : 'contain'} />
      </div>
    );
  } else if (mediaType === 'video' && posterUrl && videoUrl) {
    media = (
      <div className="media-section__media">
        <Video
          posterUrl={posterUrl}
          posterAlt={posterAlt}
          videoUrl={videoUrl}
          aspectRatio="16 / 9"
          playButtonSize="lg"
          playBehavior="inline"
        />
      </div>
    );
  } else if (mediaType === 'backgroundVideo' && backgroundVideoUrl) {
    const controlLabel = isPlaying ? 'Pause background video' : 'Play background video';
    media = (
      <div className="media-section__bg-video">
        {/* Decorative ambient motion; captions track satisfies media-a11y lint. */}
        <video
          ref={setVideoRef}
          className="media-section__bg-video-el"
          src={backgroundVideoUrl}
          poster={backgroundVideoPosterUrl}
          aria-label={backgroundVideoLabel || undefined}
          muted
          loop
          playsInline
          preload="auto"
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
        >
          <track kind="captions" />
        </video>
        <button
          type="button"
          className="media-section__bg-video-control"
          onClick={togglePlay}
          aria-label={controlLabel}
          aria-pressed={isPlaying}
        >
          <VideoButton size="xs" icon={isPlaying ? 'pause' : 'play'} />
        </button>
      </div>
    );
  }

  const classes = [
    'media-section',
    `media-section--${mediaType}`,
    fullBleed ? 'media-section--full-bleed' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <section className={classes}>
      <div className="media-section__container">{media}</div>
    </section>
  );
};
